import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { PacAction, PacBeat, PacBeatContext, PacWidget } from '@bariendo/lib/patient-access-script';
import { Box, Button, Group, Text } from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import { ReactNode, useEffect, useState } from 'react';
import { PatientAccessCallBeat } from './PatientAccessCallBeat';
import { PatientAccessCallWidget } from './PatientAccessCallWidgets';

const BORDER = '1px solid var(--mantine-color-gray-2)';

interface PatientAccessCallCardProps<T extends PatientAccessCallAnswers> {
  /** The beats to render, in order. One script per card type. */
  script: readonly PacBeat[];
  answers: T;
  /** What a beat's `disabledReason` reads besides the answers — age, state. */
  patient?: Patient;
  /** Facts the card looked up for beat and group rules: the track, the coverage area (BAR-2919). */
  context?: PacBeatContext;
  setValue: (id: string, value: string | string[] | boolean | undefined) => void;
  /** Collapsed one-line value for a beat. Empty means untouched, which clears the tick. */
  summarizeBeat: (beatId: string, answers: T) => string;
  onAction: (action: PacAction) => void;
  pendingAction?: PacAction;
  /** Renders the widget kinds specific to this script — computed fields and grids. */
  renderCardWidget?: (widget: PacWidget) => ReactNode;
  /** Controls above the beats, e.g. the intro call's visit-type router. */
  header?: ReactNode;
  /** Whether beats carry a completion tick. See `PatientAccessCallBeat`. */
  showBeatProgress?: boolean;
  /**
   * A saved note. Expanding it shows the worked script as a record of the call;
   * it is not an editor, and expanding does not reopen it for editing.
   */
  readOnly?: boolean;
  isSaving?: boolean;
  saveError?: boolean;
  /**
   * Why Save to chart is disabled, when it is. Shown in the footer in place of
   * the status line; the PN consultation requires an outcome before a note can
   * be charted.
   */
  saveDisabledReason?: string;
  onSave: () => void;
  /**
   * Which beat is open, or null. Pinned beats are not part of this state and are
   * never reported. Exposed because a card may want to defer work
   * until its beat is on screen — the intro call only queries consultation
   * availability while the booking beat is open.
   */
  onOpenBeatChange?: (beatId: string | null) => void;
}

/**
 * The shared spine for every Patient Access call card: the beats, the open/closed
 * row state, and the footer.
 *
 * Deliberately knows nothing about which call it is running. The script, the
 * beat summaries, the computed fields and the save behaviour all arrive as
 * props, so a second script — the warm hand-off — is a script file and a
 * wrapper rather than a second copy of this.
 *
 * The draft hook is NOT called here. Each card calls `usePatientAccessCallDraft`
 * itself and passes the answers down, because the card-specific effects that
 * pre-fill answers (the intro call's eligibility pre-fill and weight lookup)
 * need `updateAnswers` before this component would exist to provide it.
 */
export function PatientAccessCallCard<T extends PatientAccessCallAnswers>({
  script,
  answers,
  patient,
  context,
  setValue,
  summarizeBeat,
  onAction,
  pendingAction,
  renderCardWidget,
  header,
  showBeatProgress = true,
  readOnly = false,
  isSaving = false,
  saveError = false,
  saveDisabledReason,
  onSave,
  onOpenBeatChange,
}: PatientAccessCallCardProps<T>) {
  const [openBeat, setOpenBeat] = useState<string | null>(null);
  // Pinned beats open with the card and keep their own open state, outside the
  // one-open accordion, so they stay up while the other beats open and close.
  // Tracked as the pinned beats someone has closed, so a pinned beat is open by default.
  const [closedPinned, setClosedPinned] = useState<ReadonlySet<string>>(() => new Set());

  // Evaluated on every render: a rule reads live answers, so a beat greys out the
  // moment the procedure it depends on changes. Not on a saved note: it is a
  // record of the call, and today's chart can't say what applied then.
  const disabledReasons = new Map(
    script.map((beat) => [beat.id, readOnly ? undefined : beat.disabledReason?.(answers, patient, context)])
  );
  const openBeatDisabled = openBeat !== null && Boolean(disabledReasons.get(openBeat));

  // An open beat whose rule starts failing closes, and says so: a card gating a
  // fetch on the open beat must not keep fetching for one nobody can open.
  useEffect(() => {
    if (!openBeatDisabled) return;
    setOpenBeat(null);
    onOpenBeatChange?.(null);
  }, [openBeatDisabled, onOpenBeatChange]);

  const toggleBeat = (beatId: string) => {
    const next = openBeat === beatId ? null : beatId;
    setOpenBeat(next);
    onOpenBeatChange?.(next);
  };

  const togglePinned = (beatId: string) => {
    setClosedPinned((current) => {
      const next = new Set(current);
      if (!next.delete(beatId)) next.add(beatId);
      return next;
    });
  };

  return (
    <Box>
      {header}

      <Box style={header ? { borderTop: BORDER } : undefined}>
        {script.map((beat) => (
          <PatientAccessCallBeat
            key={beat.id}
            title={beat.title}
            summary={summarizeBeat(beat.id, answers)}
            disabledReason={disabledReasons.get(beat.id)}
            showProgress={showBeatProgress}
            isOpen={beat.pinned ? !closedPinned.has(beat.id) : openBeat === beat.id}
            onToggle={() => (beat.pinned ? togglePinned(beat.id) : toggleBeat(beat.id))}
          >
            {beat.items.map((widget, index) => (
              <PatientAccessCallWidget
                key={index}
                widget={widget}
                answers={answers}
                context={context}
                setValue={setValue}
                onAction={onAction}
                pendingAction={pendingAction}
                disabled={readOnly}
                renderCardWidget={renderCardWidget}
              />
            ))}
          </PatientAccessCallBeat>
        ))}
      </Box>

      <Group justify="space-between" py={10} style={{ borderTop: BORDER }}>
        <Text size="xs" c={saveError ? 'red.7' : 'dimmed'}>
          {readOnly
            ? 'Saved to chart'
            : saveError
              ? "Couldn't autosave — your answers are still here"
              : (saveDisabledReason ?? '')}
        </Text>
        {!readOnly && (
          <Button
            size="compact-sm"
            color="green.9"
            loading={isSaving}
            disabled={Boolean(saveDisabledReason)}
            onClick={onSave}
          >
            Save to chart
          </Button>
        )}
      </Group>
    </Box>
  );
}
